import { ApiError } from "./httpClient";

/** Waits between attempts; its length is the number of retries. */
export const SAVE_RETRY_DELAYS_MS = [500, 1_500] as const;

/**
 * A failure the same request may survive on a second try: a dropped
 * connection (fetch rejects with a plain `TypeError`, not an `ApiError`), a
 * timeout, throttling, a 5xx, or a 401 from a token that expired mid-session
 * (every request asks the auth bridge for a fresh token, so the retry carries
 * a new one). Any other 4xx is the request itself and fails at once.
 */
export const isTransientSaveError = (err: unknown): boolean => {
  if (!(err instanceof ApiError)) return true;
  return (
    err.status === 401 ||
    err.status === 408 ||
    err.status === 429 ||
    err.status >= 500
  );
};

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => window.setTimeout(resolve, ms));

/** Run `send`, retrying transient failures with the delays above. */
export const withSaveRetry = async <T>(
  send: () => Promise<T>,
  delays: readonly number[] = SAVE_RETRY_DELAYS_MS,
): Promise<T> => {
  for (let attempt = 0; ; attempt += 1) {
    try {
      return await send();
    } catch (err) {
      if (attempt >= delays.length || !isTransientSaveError(err)) throw err;
      await sleep(delays[attempt]);
    }
  }
};

export const saveErrorMessage = (err: unknown): string =>
  err instanceof ApiError
    ? err.message
    : err instanceof Error
      ? err.message
      : "Save failed";
